/**
 * @vibesites-american-landmark/theme-preferred
 *
 * American Landmark company theme built on the preferred base theme.
 * Provides consistent styling across all American Landmark properties.
 *
 * This theme:
 * - Extends @vibesites/theme-preferred
 * - Applies American Landmark brand colors and typography
 * - Can override specific components or page compositions
 */

import { mergePages, resolvePageTokens } from '@vibesites/runtime';
import type { PageComposition } from '@vibesites/runtime';
import pagesDataJson from './pages.json';

export { registry } from './registry';
export { tokens } from './tokens';
export { meta } from './meta';
export {
  componentRequirements,
  getComponentRequirements,
  getRegisteredComponentNames,
} from './requirements';

/**
 * Page compositions for the American Landmark theme.
 * Built from this theme's own `src/pages.json` using the runtime's
 * `mergePages` + `resolvePageTokens` pipeline — same logic the base theme
 * uses in its `pages.ts`, inlined here so the company theme exports `pages`
 * and the runtime picks up overrides like custom sections.
 */
const resolvedPages = mergePages(pagesDataJson as Parameters<typeof mergePages>[0]);
export const pages: Record<string, PageComposition> = Object.fromEntries(
  resolvedPages.map((p) => [p.alias, resolvePageTokens(p)]),
);

// Re-export types for convenience
export type { PageComposition, ComponentRegistry, ThemeMeta } from '@vibesites/runtime';
